import React, { CSSProperties, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import ToggleIconButton from "../../../components/buttons/toggleIconButton";
import { useTheme } from "../../../context/useThemeContext";
import { hexToRGBA } from "../../../utility/hexToRGB";
import styled from "styled-components";
import useDataStore from "../../../stores/dataStore";
import useMainStore from "../../../stores/mainStore";
import useAbsoluteWindowStore from "../../../stores/absoluteWindowStore";
import NoteContextMenuWrapper from "../../leftSidebar/notes/note/NoteContextMenuWrapper";

const TextTabStyled = styled.div<{
    $BGColor: string;
    $hoverBGColor: string;
    $disableCloseBTN?: boolean;
}>`
    background-color: ${(props) => props.$BGColor} !important;
    .closeButton {
        opacity: 0;
        transition: all 0.1s ease-in-out;

        display: ${(props) => (props.$disableCloseBTN ? "none" : "unset")};
    }
    &:hover {
        background-color: ${(props) => props.$hoverBGColor} !important;

        .separator {
            opacity: 0 !important;
        }

        .closeButton {
            opacity: 1;
        }
    }
`;

export function NoteTab(props: {
    noteID: string;
    isSelected: boolean;
    overrideHideSeparator?: boolean;
    setSelectedTab: () => void;
}) {
    // Context
    const { theme } = useTheme();

    // Store
    const noteTitle = useDataStore(
        (state) => state.data.notes[props.noteID]?.title || "undefined"
    );
    const changesMade = useDataStore(
        (state) => state.data.notes[props.noteID]?.changesMade || false
    );
    const windowShowing = useAbsoluteWindowStore(
        (state) => state.windowShowing
    );

    // Ref
    const tabRef = useRef<HTMLDivElement>(null);

    // State
    const [isRenaming, setIsRenaming] = useState(false);


    function CloseTab() {
        useMainStore.getState().remove.closeNote(props.noteID);
    }

    function DragStart(e: React.DragEvent<HTMLDivElement>) {
        // Disable mouseEvents on EVERYTHING but tabs
        document.body.style.pointerEvents = "none";
        document.getElementById("tabContainer")!.style.pointerEvents = "all";

        const dataTransfer = e.nativeEvent.dataTransfer;
        const dragData = { tabID: props.noteID, type: "Tab" };
        dataTransfer!.setData("application/json", JSON.stringify(dragData));

        const x = e.clientX; // - dragStartX;
        const y = e.clientY; // - dragStartY;
        const rect = tabRef.current?.getBoundingClientRect();
        const offsetX = e.clientX - rect!.left || 0;
        const offsetY = e.clientY - rect!.top || 0;

        useAbsoluteWindowStore.getState().setPosition(x, y);
        useAbsoluteWindowStore.getState().setWindowShowing({
            tabID: props.noteID,
            type: "Tab",
            width: rect?.width || 200,
            offset: { x: offsetX, y: offsetY },
        });

        function Drag(e: MouseEvent) {
            const x = e.clientX; // - dragStartX;
            const y = e.clientY; // - dragStartY;
            useAbsoluteWindowStore.getState().setPosition(x, y);
        }

        function DragEnd(e: MouseEvent) {
            document.removeEventListener("mousemove", Drag);
            document.removeEventListener("mouseup", DragEnd);

            useAbsoluteWindowStore.getState().setWindowShowing(undefined);
            // useMainStore.getState().endDragTab();

            document.body.style.pointerEvents = "unset";
            document.getElementById("tabContainer")!.style.pointerEvents =
                "unset";
        }

        document.addEventListener("mousemove", Drag);
        document.addEventListener("mouseup", DragEnd);
    }

    function GetHoverColor() {
        if (windowShowing && windowShowing.type === "Tab") {
            return "tranpsarent";
        } else if (props.isSelected) {
            return theme.secondaryHeaderColor;
        } else {
            return hexToRGBA(theme.secondaryHeaderColor, 0.6);
        }
    }

    function ContextMenu(e: React.MouseEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();

        e.preventDefault();
        e.stopPropagation();
        useAbsoluteWindowStore.getState().setPosition(e.clientX, e.clientY);
        useAbsoluteWindowStore.getState().setWindowShowing({
            type: "ContextMenu",
            children: (
                <NoteContextMenuWrapper
                    noteID={props.noteID}
                    isTabContextMenu
                    setIsRenaming={setIsRenaming}
                />
            ),
        });
    }

    return (
        <TextTabStyled
            draggable
            ref={tabRef}
            $disableCloseBTN={windowShowing?.type === "Tab"}
            onContextMenu={ContextMenu}
            onDragStart={(e) => {
                e.preventDefault();
                e.stopPropagation();

                DragStart(e);
            }}
            onMouseDown={(e) => {
                // e.preventDefault();
                if (e.button === 1) CloseTab();
                else if (e.button === 0) {
                    // DragStart(e);
                }
            }}
            onClick={(e) => {
                if (props.isSelected) return;

                props.setSelectedTab();
            }}
            $hoverBGColor={GetHoverColor()}
            $BGColor={
                props.isSelected
                    ? theme.secondaryHeaderColor
                    : theme.headerColor
            }
            // hoverBGColor={props.isSelected ? theme.secondaryHeaderColor : hexToRGBA(theme.headerColor, 0.4)}
            className="w-[210px] min-w-[120px] h-[80%] bg-black rounded-t-md flex items-center pl-3 pr-2 relative"
            style={{
                marginTop: "auto",
                color: theme.fontColor[50],
                opacity:
                    props.noteID ===
                    (windowShowing?.type !== "Tab" || windowShowing?.tabID)
                        ? 0.25
                        : 1,
                transition: "all 0.1s ease-in-out",
            }}
        >
            <HoverContainer containerPosition="left" tabID={props.noteID} />
            <HoverContainer containerPosition="right" tabID={props.noteID} />
            <div
                className="changesMade h-[6px] w-[6px]  rounded-full mr-2"
                style={{
                    backgroundColor: theme.fontColor[50],
                    display: changesMade ? "block" : "none",
                }}
            />

            <p className="tabTitle block text-sm flex-1 overflow-hidden mr-1 text-ellipsis whitespace-nowrap">
                {noteTitle}
            </p>
            {/* Hide/Show handled in TextTabStyled */}
            <div className="closeButton">
                <ToggleIconButton
                    alt="Close tab"
                    icon={<IoClose />}
                    size={{ x: 20, y: 20 }}
                    iconSize={16}
                    inactiveIconColor={theme.fontColor[100]}
                    hoverBGInactive={hexToRGBA(theme.fontColor[100], 0.15)}
                    onClick={(e) => {
                        e.stopPropagation();
                        CloseTab();
                    }}
                />
            </div>
            <div
                className="separator h-[16px] w-[1px] absolute right-0"
                style={{
                    backgroundColor: theme.fontColor[100],
                    opacity:
                        props.overrideHideSeparator || props.isSelected
                            ? 0
                            : 0.2,
                }}
            ></div>
        </TextTabStyled>
    );
}

/**
 * A component that provides a hoverable container for rearranging tabs.
 *
 * @param {"left" | "right"} props.containerPosition - The position of the container, either "left" or "right".
 * @param {string} props.tabID - The unique identifier of the tab associated with this container.
 *
 * @description
 * This component displays a hoverable indicator on either side of a tab, allowing users to drag and
 * drop tabs to reorder them. The styling and visibility of the indicator depend on the hover state
 * and the current window showing. It manages hover state internally and updates the order of opened
 * tabs upon releasing the mouse button.
 */

function HoverContainer(props: {
    containerPosition: "left" | "right";
    tabID: string;
}) {
    // State
    const [isHover, setIsHover] = useState(false);

    const { theme } = useTheme();
    const openedNoteIDs = useMainStore((state) => state.data.openedNoteIDs);
    const windowShowing = useAbsoluteWindowStore(
        (state) => state.windowShowing
    );

    function getStyling(): CSSProperties {
        if (props.containerPosition === "left") {
            return {
                borderLeft: "2px solid " + hexToRGBA(theme.accentColor, 1),
                left: "-2px",
                right: "50%",
                display:
                    windowShowing?.type === "Tab" &&
                    windowShowing?.tabID !== props.tabID
                        ? "flex"
                        : "none",
                opacity: isHover ? 1 : 0,
            };
        } else {
            return {
                borderRight: "2px solid " + hexToRGBA(theme.accentColor, 1),
                right: "-2px",
                left: "50%",
                display:
                    windowShowing?.type === "Tab" &&
                    windowShowing?.tabID !== props.tabID
                        ? "flex"
                        : "none",
                opacity: isHover ? 1 : 0,
            };
        }
    }

    return (
        <div
            className="hoverIndicator absolute top-0 bottom-0"
            style={getStyling()}
            onMouseEnter={(e) => {
                e.preventDefault();
                if (
                    windowShowing?.type !== "Tab" ||
                    windowShowing?.tabID === props.tabID
                ) {
                    setIsHover(false);
                    return;
                }

                if (windowShowing?.type !== "Tab" || windowShowing?.tabID) {
                    setIsHover(true);
                }
            }}
            onMouseLeave={(e) => {
                e.preventDefault();
                setIsHover(false);
            }}
            onMouseUp={(e) => {
                // Drop on left side
                e.preventDefault();
                if (windowShowing?.type !== "Tab" || !windowShowing.tabID)
                    return;

                const tempOpenTabsList = [...openedNoteIDs];
                const indexDraggedTab = openedNoteIDs.indexOf(
                    windowShowing?.tabID!
                );

                // Remove the dragged tab
                tempOpenTabsList.splice(indexDraggedTab, 1);

                const indexCurrentTab = tempOpenTabsList.indexOf(props.tabID);

                // If dragged over left, insert it BEFORE the current tab. Else, insert it AFTER
                if (props.containerPosition === "left") {
                    // Insert tab BEFORE the current tab
                    tempOpenTabsList.splice(
                        indexCurrentTab,
                        0,
                        windowShowing?.tabID!
                    );
                } else {
                    // Insert tab After the current tab
                    tempOpenTabsList.splice(
                        indexCurrentTab + 1,
                        0,
                        windowShowing?.tabID!
                    );
                }

                useMainStore
                    .getState()
                    .set.openedNoteIDs(() => tempOpenTabsList);
            }}
        />
    );
}
